import { Group, Button, Menu, ActionIcon } from "@mantine/core";
import { IconDotsVertical } from "@tabler/icons-react";

/**
 * An action cluster that stays tidy on a phone. On desktop it renders `children` exactly as given
 * (so the existing look is preserved — no regression). On mobile it renders a compact row: the
 * primary call-to-action inline, and every secondary action folded into a single kebab Menu, so a
 * header's buttons stop wrapping into a ragged two-row block.
 *
 *   <OverflowActions
 *     primary={{ label, icon, onClick, color?, disabled? }}
 *     actions={[{ label, icon, onClick, color? }]}
 *   >
 *     …desktop inline buttons/links…
 *   </OverflowActions>
 */
export default function OverflowActions({ primary, actions = [], children, ...groupProps }) {
  return (
    <>
      {/* Desktop: the caller's own cluster, untouched. */}
      <Group gap="md" wrap="nowrap" align="center" visibleFrom="sm" {...groupProps}>
        {children}
      </Group>

      {/* Mobile: primary inline + everything else in a kebab. */}
      <Group gap="xs" wrap="nowrap" align="center" hiddenFrom="sm" {...groupProps}>
        {primary && (
          <Button
            size="sm"
            color={primary.color}
            disabled={primary.disabled}
            leftSection={primary.icon}
            onClick={primary.onClick}
          >
            {primary.label}
          </Button>
        )}
        {actions.length > 0 && (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="lg" aria-label={primary?.moreLabel ?? "More"}>
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {actions.map((a, i) => (
                <Menu.Item key={i} color={a.color} leftSection={a.icon} onClick={a.onClick}>
                  {a.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
        )}
      </Group>
    </>
  );
}
